import { HorizontalAlignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button } from "../button/Button";
import { ButtonVariant } from "../button/ButtonVariant";
import { Checkbox, RadioGroup, Switch } from "../..";
import { Component } from "../../base/Component";
import { ComponentDeserializer, DeserializeProps } from "../../serializer/ComponentDeserializer";
import { ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { DataSource, DataSourceAction } from "../../databinding/DataSource";
import { DataSourceExecutionEvent } from "../../events/DataSourceExecutionEvent";
import { DesignerInterface } from "../../base/DesignerInterface";
import { FieldsPanel } from "./FieldsPanel";
import { HorizontalSpacer } from "../../page/HorizontalSpacer";
import { Label } from "../label/Label";
import { LabelVariant } from "../label/LabelVariant";
import { Panel } from "../panel/Panel";
import { PanelProps } from "../panel/PanelProps";
import { ResetFormButton } from "../../page/ResetFormButton";
import { SearchButton } from "../../page/SearchButton";
import { SearchPanelPropDefinitions, SearchPanelProps } from "./SearchPanelProps";
import { serializeComponents } from "../../serializer/ComponentSerializer";
import { Table } from "../table/Table";
import { TransitionOptions } from "../../base/TransitionOptions";

enum InnerIds {
    BUTTON_EXPAND = "buttonExpand",
    BUTTON_RESET = "buttonReset",
    BUTTON_SEARCH = "buttonSearch",
    LABEL_TITLE = "labelTitle",
    PANEL_BUTTONS = "panelButtons",
    PANEL_COLLAPSIBLE = "panelCollapsible",
    PANEL_FIELDS = "panelFields"
}

export class SearchPanel extends Panel implements SearchPanelProps {
    private buttonExpand: Button;
    private buttonResetForm: ResetFormButton;
    private buttonSearch: SearchButton;
    private expanded = true;
    private labelTitle: Label;
    private panelButtons: Panel;
    private panelCollapsible: Panel;
    private panelFields: Panel;

    constructor(props?: Partial<SearchPanelProps>) {
        super(props, false);
        this.createTitleLabel(props?.title);
        this.createExpandButton();
        this.createFieldsPanel();
        this.createResetButton();
        this.createSearchButton();
        this.createButtonsPanel();
        this.panelButtons.add(new HorizontalSpacer(), this.buttonResetForm, this.buttonSearch);
        this.createCollapsiblePanel();
        this.panelCollapsible.add(this.panelFields, this.panelButtons);
        this.setProps({ ...this.defaultProps, ...props });
        this.addPropertyDefaultValueOverrides({ ...this.defaultProps });
        this.add(this.labelTitle, new HorizontalSpacer(), this.buttonExpand, this.panelCollapsible);
    }

    private get defaultProps(): Partial<PanelProps> {
        return {
            borderColor: "strokeSecondary",
            borderRadius: 4,
            borderWidth: 1,
            fillRow: true,
            marginBottom: 16,
            padding: 0
        };
    }

    private getInnerComponents(): Component[] {
        return [
            this.buttonExpand,
            this.buttonResetForm,
            this.buttonSearch,
            this.labelTitle,
            this.panelButtons,
            this.panelCollapsible,
            this.panelFields
        ];
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.buttonExpand, InnerIds.BUTTON_EXPAND);
        this.setInnerComponentId(this.buttonResetForm, InnerIds.BUTTON_RESET);
        this.setInnerComponentId(this.buttonSearch, InnerIds.BUTTON_SEARCH);
        this.setInnerComponentId(this.labelTitle, InnerIds.LABEL_TITLE);
        this.setInnerComponentId(this.panelButtons, InnerIds.PANEL_BUTTONS);
        this.setInnerComponentId(this.panelCollapsible, InnerIds.PANEL_COLLAPSIBLE);
        this.setInnerComponentId(this.panelFields, InnerIds.PANEL_FIELDS);
    }

    private createTitleLabel(title: string) {
        this.labelTitle = new Label({
            id: this.createInnerComponentId(InnerIds.LABEL_TITLE),
            variant: LabelVariant.pageTitle,
            text: title,
            rowBreak: false,
            onClick: this.buttonExpandOnClick,
            _designer: this._designer
        });
    }

    private createExpandButton() {
        this.buttonExpand = new Button({
            id: this.createInnerComponentId(InnerIds.BUTTON_EXPAND),
            onClick: this.buttonExpandOnClick,
            imageName: ImageName.chevron,
            imageTransition: "transform 400ms ease-in-out",
            align: HorizontalAlignment.RIGHT,
            fontSize: "xlarge",
            size: "large",
            color: "subtle.darker",
            variant: ButtonVariant.round,
            _designer: this._designer
        });
    }

    private createFieldsPanel() {
        this.panelFields = new FieldsPanel(this, { id: this.createInnerComponentId(InnerIds.PANEL_FIELDS) });
    }

    private createButtonsPanel() {
        this.panelButtons = new Panel({
            id: this.createInnerComponentId(InnerIds.PANEL_BUTTONS),
            fillRow: true,
            margin: 0,
            paddingLeft: 8,
            paddingRight: 8,
            _designer: this._designer
        });
        this.panelButtons.allowDropInDesigner = false;
    }

    private createResetButton() {
        this.buttonResetForm = new ResetFormButton({
            id: this.createInnerComponentId(InnerIds.BUTTON_RESET),
            rowBreak: false,
            dataSource: this.dataSource,
            _designer: this._designer
        });
        this.buttonResetForm.doAfterReset = () => this.doAfterReset();
    }

    public resetDefaults() {
        this.buttonResetForm?.resetDefaults();
    }

    private createSearchButton() {
        this.buttonSearch = new SearchButton({
            id: this.createInnerComponentId(InnerIds.BUTTON_RESET),
            marginRight: 8,
            busyWhenDataSourceBusy: true,
            default: true,
            dataSource: this.dataSource,
            _designer: this._designer
        });
    }

    private createCollapsiblePanel() {
        this.panelCollapsible = Panel.createNoMarginNoPaddingPanel({
            id: this.createInnerComponentId(InnerIds.PANEL_COLLAPSIBLE),
            fillRow: true,
            _designer: this._designer
        });
        this.panelCollapsible.allowDropInDesigner = false;
    }

    public get title(): string {
        return this.labelTitle.text;
    }

    public set title(value: string) {
        this.labelTitle.text = value;
    }

    override get dataSource(): DataSource {
        return super.dataSource;
    }

    override set dataSource(value: DataSource) {
        super.dataSource = value;
        this.getInnerComponents()
            .filter(component => !(component instanceof Panel))
            .forEach(component => component.dataSource = this.dataSource);
        this.dataSource?.addAfterExecutionListener(this.dataSourceDoAfterExecution);
    }

    private buttonExpandOnClick = () => {
        if (this.expanded === true) {
            this.collapse();
        }
        else {
            this.expanded = true;
            this.setChevronImageRotation();
            this.expand();
        }
    }

    public override collapse(options?: TransitionOptions): Promise<any> {
        this.expanded = false;
        this.setChevronImageRotation();
        return this.panelCollapsible.collapse({ speed: 400, ...options });
    }

    public override async expand(options?: TransitionOptions) {
        this.panelCollapsible.expand({ speed: 400, ...options }).then(() => {
            this.focusFirstSearchField();
        });
    }

    private setChevronImageRotation() {
        this.buttonExpand.imageRotation = this.expanded ? 0 : 90;
    }

    private doAfterReset() {
        this.getParentLayout()?.findComponentsByType(Table).forEach(table => table.setFilter(null));
        this.focusFirstSearchField();
    }

    private dataSourceDoAfterExecution = (event: DataSourceExecutionEvent) => {
        if (event.getAction() === DataSourceAction.SEARCH) {
            if (event.dataSource.data.length > 0) {
                this.collapse();
            }
        }
    }

    private focusFirstSearchField() {
        this.panelFields.components.find(field => field.isFocusable())?.focus();
    }

    override get _designer(): DesignerInterface {
        return super._designer;
    }

    override set _designer(value: DesignerInterface) {
        this._shouldAddDesignerContainerProperties = false;
        super._designer = value;
        value?.addDesignerContainerProperties(this, 80, 34, null, false);
        this.getInnerComponents()?.forEach(component => {
            if (component != null) {
                component._designer = value;
                component._interactionEnabled = this._designer == null;
            }
        });
    }

    /**
     * Serialize the search components into their own array.  Do not serialize the normal components array, as it only
     * contains the inner, auto-generated components.
     * @returns the string result of the serialization
     */
    override _serializeNonProps(): string {
        let result = "";
        if (this.panelFields.components.length > 0) {
            result += "\"searchComponents\": " + serializeComponents(this.panelFields.components, null) + ",\n";
        }
        return result;
    }

    override async _deserializeSpecialProps(props: DeserializeProps): Promise<string[]> {
        this.setDataSourceFromDeserializeProps(props);
        const compDef = props.def;
        if (compDef.searchComponents != null) {
            const children = await new ComponentDeserializer({
                ...props,
                def: compDef.searchComponents,
                designer: this._designer
            }).deserialize();
            children?.forEach(component => {
                // Need a way to identify components that allowIndeterminate
                if (component instanceof Checkbox || component instanceof Switch) {
                    component.allowIndeterminate = true;
                    component.checked = undefined;
                }
                else if (component instanceof RadioGroup)
                    component.allowIndeterminate = true;
                this.panelFields.add(component);
            });
        }
        return ["dataSource", "searchComponents"];
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return SearchPanelPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "searchpanel";
    }

    override get properName(): string {
        return "Search Panel";
    }
}
ComponentTypes.registerComponentType("searchpanel", { constructor: SearchPanel.prototype.constructor });
